import { useRef } from "react"

import {
  Badge,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@coval/ui"
import { ChevronRight, History } from "lucide-react"

import { formatDate } from "../../utils/reportClient"
import type { Notebook } from "../../utils/reportTypes"
import { reportClass } from "./ReportStyles"

export function HistoryPane({
  notebook,
  revisions,
  busy,
  onClose,
  openNotebook,
}: {
  notebook: Notebook
  revisions: NonNullable<Notebook["revisions"]>
  busy: string
  onClose: () => void
  openNotebook: (id: string, revision: number) => void
}) {
  const previousFocus = useRef<HTMLElement | null>(null)
  return (
    <Drawer open onOpenChange={(open) => !open && onClose()}>
      <DrawerContent
        size="sm"
        className="print:hidden"
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          previousFocus.current =
            document.activeElement instanceof HTMLElement
              ? document.activeElement
              : null
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault()
          if (previousFocus.current?.isConnected) {
            previousFocus.current.focus({ preventScroll: true })
          }
        }}
      >
        <DrawerHeader>
          <DrawerTitle className="flex items-center gap-2">
            <History size={15} />
            Revision history
          </DrawerTitle>
          <DrawerDescription>
            Every generation and saved edit leaves a version you can return to.
          </DrawerDescription>
        </DrawerHeader>
        <DrawerBody className={reportClass("history-list")}>
          {[...revisions]
            .sort((a, b) => b.revision - a.revision)
            .map((entry) => (
              <Button
                type="button"
                variant="ghost"
                key={entry.revision}
                disabled={Boolean(busy)}
                onClick={() => openNotebook(notebook.id, entry.revision)}
              >
                <span>
                  Revision {entry.revision}
                  {entry.revision === notebook.revision && (
                    <Badge>Viewing</Badge>
                  )}
                </span>
                <time>{formatDate(entry.created_at, true)}</time>
                <ChevronRight size={13} />
              </Button>
            ))}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  )
}
